import { useState, useCallback, useMemo, useRef } from "react";
import { useList, useCustom, useApiUrl } from "@refinedev/core";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Coins } from "lucide-react";
import { cn } from "@/lib/utils";

interface Currency {
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  isActive: boolean;
}

interface ExchangeRatesResponse {
  baseCurrency: string;
  rates: Record<string, number>;
  provider: string;
  fetchedAt: string;
}

export interface MultiCurrencyPricesValue {
  price: number | null;
  currency: string;
  prices: Record<string, number> | null;
  baseCurrency: string;
}

interface MultiCurrencyPricesProps {
  value: MultiCurrencyPricesValue;
  onChange: (value: MultiCurrencyPricesValue) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Компонент для мультивалютного ценообразования
 * Позволяет задать цену в базовой валюте и автоматически рассчитать цены в других валютах
 * или задать цены вручную для каждой валюты
 */
export function MultiCurrencyPrices({
  value,
  onChange,
  label = "Ценообразование",
  required = false,
  disabled = false,
}: MultiCurrencyPricesProps) {
  const apiUrl = useApiUrl();

  // Загружаем валюты
  const { query: { data: currenciesResult, isLoading: currenciesLoading } } = useList<Currency>({
    resource: "currencies",
  });

  const currencies = useMemo(
    () => currenciesResult?.data ?? [],
    [currenciesResult?.data]
  );

  // Загружаем курсы от RUB (CBR покрывает все наши валюты: UZS, USD, EUR)
  const { query: { data: rubRatesResult, isLoading: rubRatesLoading } } = useCustom<ExchangeRatesResponse>({
    url: `${apiUrl}/currencies/rates/RUB`,
    method: "get",
  });

  const rubRates = useMemo(
    () => rubRatesResult?.data?.rates ?? {},
    [rubRatesResult?.data?.rates]
  );

  const ratesLoading = rubRatesLoading;

  // Форматирование числа с пробелами-разделителями тысяч
  const formatPrice = useCallback((num: number | string, decimalPlaces: number): string => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(n)) { return '0'; }
    const fixed = n.toFixed(decimalPlaces);
    const [intPart, decPart] = fixed.split('.');
    const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return decPart ? `${formatted}.${decPart}` : formatted;
  }, []);

  // Убрать форматирование — оставить только цифры и точку
  const parsePrice = useCallback((str: string): string => {
    return str.replace(/\s/g, '');
  }, []);

  // Найти decimalPlaces для валюты
  const getDecimalPlaces = useCallback((code: string): number => {
    const currency = currencies.find((c) => c.code === code);
    return currency?.decimalPlaces ?? 2;
  }, [currencies]);

  // Локальное состояние для редактирования цен (хранит форматированные строки)
  // Используем ref для отслеживания "последнего внешнего значения", чтобы обновлять
  // localPrices только при реальном внешнем изменении, а не при собственном onChange
  const lastExternalPricesRef = useRef<string>('');
  const [localPrices, setLocalPrices] = useState<Record<string, string>>({});

  // Синхронизация при внешнем изменении (без useEffect, чтобы избежать лишних ре-рендеров)
  const currentPricesKey = JSON.stringify(value.prices) + '|' + String(value.price) + '|' + value.currency;
  if (currentPricesKey !== lastExternalPricesRef.current) {
    lastExternalPricesRef.current = currentPricesKey;
    const newLocalPrices: Record<string, string> = {};
    if (value.prices) {
      for (const [code, price] of Object.entries(value.prices)) {
        if (typeof price !== 'number') { continue; }
        newLocalPrices[code] = formatPrice(price, getDecimalPlaces(code));
      }
    } else if (value.price !== null) {
      newLocalPrices[value.currency] = formatPrice(value.price, getDecimalPlaces(value.currency));
    }
    setLocalPrices(newLocalPrices);
  }

  // Получить курс между любыми двумя валютами через RUB как промежуточную
  const getRate = useCallback((from: string, to: string): number | null => {
    if (from === to) { return 1; }

    // rubRates содержит курсы: 1 RUB = X (currency)
    // Чтобы конвертировать from → to: (amount_from / rubRates[from]) * rubRates[to]
    // Т.е. rate = rubRates[to] / rubRates[from]

    if (from === 'RUB' && rubRates[to]) {
      return rubRates[to];
    }

    if (to === 'RUB' && rubRates[from]) {
      return 1 / rubRates[from];
    }

    const fromInRub = rubRates[from];
    const toInRub = rubRates[to];
    if (fromInRub && toInRub) {
      return toInRub / fromInRub;
    }

    return null;
  }, [rubRates]);

  // Обновить цену — при изменении любой валюты пересчитать все остальные через курсы
  const handlePriceChange = (currencyCode: string, rawInput: string) => {
    // Убираем пробелы-разделители, оставляем цифры и точку
    const cleanInput = parsePrice(rawInput);
    const parsedPrice = parseFloat(cleanInput);
    const validPrice = !isNaN(parsedPrice) && parsedPrice > 0 ? parsedPrice : null;

    // Если значение невалидно — обновляем только локальное поле ввода (без форматирования, чтобы не мешать вводу)
    if (validPrice === null) {
      setLocalPrices((prev) => ({ ...prev, [currencyCode]: rawInput }));
      return;
    }

    const newLocalPrices: Record<string, string> = {
      // Для редактируемого поля сохраняем ввод пользователя как есть (форматируем onBlur)
      [currencyCode]: rawInput,
    };
    const newPrices: Record<string, number> = {
      [currencyCode]: validPrice,
    };

    // Пересчитать все остальные валюты через курс от изменённой валюты
    for (const currency of currencies) {
      if (currency.code === currencyCode) { continue; }
      const rate = getRate(currencyCode, currency.code);
      if (rate !== null) {
        const calculated = Math.round(validPrice * rate * Math.pow(10, currency.decimalPlaces)) / Math.pow(10, currency.decimalPlaces);
        newLocalPrices[currency.code] = formatPrice(calculated, currency.decimalPlaces);
        newPrices[currency.code] = calculated;
      }
    }

    setLocalPrices(newLocalPrices);

    const basePrice = newPrices[value.baseCurrency] ?? null;

    const newValue = {
      ...value,
      price: basePrice,
      prices: newPrices,
    };
    // Обновляем ref чтобы следующий рендер не считал это "внешним изменением"
    lastExternalPricesRef.current = JSON.stringify(newValue.prices) + '|' + String(newValue.price) + '|' + newValue.currency;
    onChange(newValue);
  };

  // При потере фокуса — форматировать введённое значение
  const handleBlur = (currencyCode: string) => {
    const raw = localPrices[currencyCode];
    if (!raw) { return; }
    const num = parseFloat(parsePrice(raw));
    if (isNaN(num) || num <= 0) { return; }
    setLocalPrices((prev) => ({
      ...prev,
      [currencyCode]: formatPrice(num, getDecimalPlaces(currencyCode)),
    }));
  };

  // Изменить базовую валюту
  const handleBaseCurrencyChange = (newBaseCurrency: string) => {
    // Валидация: новая baseCurrency должна быть в prices (если prices заданы)
    if (value.prices && Object.keys(value.prices).length > 0 && !(newBaseCurrency in value.prices)) {
      // Если цены для новой baseCurrency нет — рассчитываем через текущий курс
      const rate = getRate(value.baseCurrency, newBaseCurrency);
      if (rate === null) {return;} // Нет курса — смена невозможна
    }

    // Определяем цену в новой базовой валюте
    let newBasePrice: number | null = value.prices?.[newBaseCurrency] ?? null;

    // Если цены нет в prices — рассчитываем через курс
    if (newBasePrice === null && value.price !== null) {
      const rate = getRate(value.baseCurrency, newBaseCurrency);
      if (rate !== null) {
        newBasePrice = Math.round(value.price * rate * 100) / 100;
      }
    }

    // Пересчитываем все цены через курсы относительно новой базовой цены
    const newPrices: Record<string, number> = {};

    if (newBasePrice !== null) {
      newPrices[newBaseCurrency] = newBasePrice;

      // Пересчитываем остальные валюты
      const currentPrices = value.prices ?? {};
      for (const code of Object.keys(currentPrices)) {
        if (code === newBaseCurrency) {continue;}

        // Используем текущий курс для пересчёта
        const rate = getRate(newBaseCurrency, code);
        if (rate !== null) {
          newPrices[code] = Math.round(newBasePrice * rate * 100) / 100;
        }
      }
    }

    const newValue = {
      ...value,
      baseCurrency: newBaseCurrency,
      currency: newBaseCurrency,
      price: newBasePrice,
      prices: Object.keys(newPrices).length > 0 ? newPrices : value.prices,
    };
    lastExternalPricesRef.current = JSON.stringify(newValue.prices) + '|' + String(newValue.price) + '|' + newValue.currency;
    onChange(newValue);
  };

  const isLoading = currenciesLoading || ratesLoading;

  const FALLBACK_CURRENCIES = [
    { code: 'UZS', name: 'Узбекский сум', symbol: 'сум' },
    { code: 'USD', name: 'Доллар США', symbol: '$' },
    { code: 'RUB', name: 'Российский рубль', symbol: '₽' },
    { code: 'EUR', name: 'Евро', symbol: '€' },
  ];

  // Если нет валют, показываем простое поле цены
  if (!isLoading && currencies.length === 0) {
    return (
      <div className="space-y-2">
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Цена</Label>
            <Input
              type="number"
              step="0.01"
              value={value.price ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  price: e.target.value ? parseFloat(e.target.value) : null,
                })
              }
              placeholder="0.00"
              disabled={disabled}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Валюта</Label>
            <Select
              value={value.currency}
              onValueChange={(v) => onChange({ ...value, currency: v, baseCurrency: v })}
            >
              <SelectTrigger disabled={disabled}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FALLBACK_CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Coins className="h-4 w-4" />
            {label}
            {required && <span className="text-destructive">*</span>}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Выбор базовой валюты */}
        <div className="flex items-center gap-4">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">
            Базовая валюта:
          </Label>
          <Select
            value={value.baseCurrency}
            onValueChange={handleBaseCurrencyChange}
            disabled={disabled}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} ({c.symbol})
                </SelectItem>
              ))}
              {currencies.length === 0 && (
                <>
                  <SelectItem value="UZS">UZS</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="RUB">RUB</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Цены по валютам */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {(currencies.length > 0 ? currencies : [
            { code: "UZS", name: "Узбекский сум", symbol: "сум", decimalPlaces: 0, isActive: true },
            { code: "USD", name: "Доллар США", symbol: "$", decimalPlaces: 2, isActive: true },
            { code: "RUB", name: "Российский рубль", symbol: "₽", decimalPlaces: 2, isActive: true },
          ]).map((c: Currency) => {
            const isBase = c.code === value.baseCurrency;
            const rate = getRate(value.baseCurrency, c.code);

            return (
              <div
                key={c.code}
                className={cn(
                  "space-y-1 p-3 rounded-lg border",
                  isBase && "bg-primary/5 border-primary/20"
                )}
              >
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">
                    {c.code}
                    {isBase && (
                      <span className="ml-1 text-primary text-[10px]">(базовая)</span>
                    )}
                  </Label>
                  <span className="text-xs text-muted-foreground">{c.symbol}</span>
                </div>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={localPrices[c.code] ?? ""}
                  onChange={(e) => handlePriceChange(c.code, e.target.value)}
                  onBlur={() => handleBlur(c.code)}
                  placeholder="0"
                  disabled={disabled}
                  className="h-8"
                />
                {!isBase && rate && (
                  <p className="text-[10px] text-muted-foreground">
                    Курс: 1 {value.baseCurrency} = {rate.toFixed(4)} {c.code}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground">
          Введите цену в любой валюте — остальные пересчитаются автоматически по текущему курсу.
        </p>
      </CardContent>
    </Card>
  );
}
